import React, { useMemo, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  ColDef,
  ICellRendererParams,
  ModuleRegistry,
  RowStyle,
} from 'ag-grid-community';
import { AllCommunityModule } from 'ag-grid-community';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import './ag-grid-table.scss';
import { SHARED_SVGS } from '../assets';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

interface ColumnConfig {
  name: string;
  alignment?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';
  isVisible?: boolean;
  color?: string;
  headerColor?: string;
  headerFontSize?: number;
  enableGroup?: boolean;
  size?: number;
  minSize?: number;
  maxSize?: number;
  flex?: number;
}

interface AgGridTableProps {
  columnConfig: Record<string, ColumnConfig>;
  data: any[];
  customCellStyles: React.CSSProperties;
  groupRows?: boolean;
  columnType?: string;
  fillGridWidth?: boolean;
  initialSortState?: { colId: string; sort: 'asc' | 'desc' }[];
  columnComparators?: Record<string, (valueA: any, valueB: any) => number>;
}

export const AgGridTable: React.FC<AgGridTableProps> = ({
  columnConfig,
  data,
  groupRows = false,
  columnType,
  customCellStyles,
  fillGridWidth = false,
  initialSortState,
  columnComparators,
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [extendedIds, setExtendedIds] = useState<string[]>([]);

  const cellRenderer = (params: ICellRendererParams, config: ColumnConfig) => {
    const value = config?.enableGroup
      ? params?.value?.value
      : (params?.value ?? '');
    if (React.isValidElement(value)) {
      return <>{value}</>;
    }
    return (
      <span className="primary-font-regular" style={customCellStyles}>
        {value ?? ''}
      </span>
    );
  };

  const customGroupRenderer = (
    params: ICellRendererParams,
    config: ColumnConfig
  ) => {
    if (params?.data?.isParent && config?.enableGroup) {
      return (
        <div
          className="custom-group-cell"
          onClick={() => {
            if (extendedIds?.includes(params?.data?.__rowId)) {
              setExtendedIds(
                extendedIds?.filter((id) => id !== params?.data?.__rowId)
              );
            } else {
              setExtendedIds([...extendedIds, params?.data?.__rowId]);
            }
          }}
        >
          <div style={{ width: 30 }}>
            <img
              className="group-toggle"
              src={
                extendedIds?.includes(params?.data?.__rowId)
                  ? SHARED_SVGS.ArrowDownDark
                  : SHARED_SVGS.ArrowRightBlack
              }
              alt=""
            />
          </div>
          <span className="group-label">
            {params?.value?.value ?? params?.value ?? '-'}
          </span>
          <span className="group-count">
            {params?.value?.groupLabel
              ? params?.value?.groupLabel(params?.data?.groupCount ?? 0)
              : ''}
          </span>
        </div>
      );
    }
    return (
      <>{config?.enableGroup ? '' : (cellRenderer(params, config) ?? '')}</>
    );
  };

  const rowData = useMemo(() => {
    if (groupRows && columnType) {
      const goupByAllProperties = data?.map((obj) => obj?.[columnType]);

      const groupBySpecificProperties = Array.from(
        new Map(
          goupByAllProperties?.map((item) => [item.groupKey, item])
        ).values()
      );
      const parentGroups = groupBySpecificProperties?.map((data, index) => ({
        __rowId: `group:${index}`,
        [columnType]: data,
        isParent: true,
      }));
      let allGroups: any[] = [];
      parentGroups?.map((obj) => {
        const groupKey = obj?.[columnType]?.groupKey;
        const childrenData = data
          ?.filter((item) => item?.[columnType]?.groupKey === groupKey)
          ?.map((data, index) => ({
            ...data,
            __rowId: `row: ${groupKey}-${index}`,
            isParent: false,
            parentId: obj?.__rowId,
          }));
        allGroups = [
          ...allGroups,
          { ...obj, groupCount: childrenData?.length },
          ...(childrenData ?? []),
        ];
      });
      const filteredData = allGroups?.filter((item) => {
        return item?.isParent || extendedIds?.includes(item?.parentId);
      });

      return filteredData;
    } else {
      return (
        data.map((data, index) => ({
          ...data,
          __rowId: `row: ${index}`,
        })) ?? []
      );
    }
  }, [data, columnType, extendedIds]);

  const columnDefs: ColDef[] = useMemo(() => {
    return Object.entries(columnConfig)
      .filter(([_, config]) => config.isVisible !== false)
      .map(([key, config]) => {
        const isGroupColumn = groupRows && columnType === key;

        const initialSort = initialSortState?.find((s) => s.colId === key);

        const colDef: ColDef = {
          headerName: config.name,
          field: key,
          cellStyle: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: config.alignment ?? 'flex-start',
            color: config.color ?? 'inherit',
          },
          headerClass: 'ag-custom-header',
          sortable: true,
          resizable: true,
          sort: initialSort?.sort ?? null,
          comparator: columnComparators?.[key],
          pinned: isGroupColumn ? 'left' : undefined,
          suppressMovable: isGroupColumn ? true : undefined,
          ...(config.size !== undefined && {
            width: config.size,
          }),
          ...(config.minSize !== undefined && {
            minWidth: config.minSize,
          }),
          ...(config.maxSize !== undefined && {
            maxWidth: config.maxSize,
          }),
          ...(config.flex !== undefined && {
            flex: config.flex,
          }),
          cellRenderer: isGroupColumn
            ? (params: ICellRendererParams<any, any, any>) =>
                customGroupRenderer(params, config)
            : (params: ICellRendererParams<any, any, any>) =>
                cellRenderer(params, config),
        };
        return colDef;
      });
  }, [
    columnConfig,
    groupRows,
    columnType,
    extendedIds,
    initialSortState,
    columnComparators,
  ]);

  const autoSizeAll = React.useCallback(() => {
    const api = gridRef.current?.api;
    const columnApi = gridRef.current?.api;
    if (!api || !columnApi) return;

    const allColIds: string[] = [];
    columnApi.getColumns()?.forEach((col) => {
      allColIds.push(col.getColId());
    });

    columnApi.autoSizeColumns(allColIds, false);

    if (columnType) {
      const c = columnApi.getColumn(columnType);
      if (c) {
        const width = Math.min(c.getActualWidth(), 320);
        columnApi.setColumnWidths([{ key: columnType, newWidth: width }]);
      }
    }

    if (fillGridWidth) {
      api.sizeColumnsToFit();
    }
  }, [
    columnConfig,
    groupRows,
    columnType,
    rowData,
    extendedIds,
    fillGridWidth,
  ]);

  return (
    <div className="ag-theme-alpine" style={{ width: '100%', height: '100%' }}>
      <AgGridReact
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        animateRows
        getRowId={(p) => p?.data?.__rowId}
        groupDisplayType="custom"
        groupDefaultExpanded={0}
        onFirstDataRendered={autoSizeAll}
        onGridSizeChanged={autoSizeAll}
        getRowStyle={(p): RowStyle => {
          if (p?.data?.isParent && extendedIds?.includes(p?.data?.__rowId)) {
            return {
              backgroundColor: '#F5F5F5',
              borderBottom: '1px solid #D9D9D9',
            };
          }

          if (p?.data?.isParent) {
            return {
              borderBottom: '1px solid #D9D9D9',
            };
          }

          return {};
        }}
        enableCellTextSelection
        getRowHeight={() => 56}
        defaultColDef={{ resizable: true }}
        autoSizeStrategy={{ type: 'fitCellContents' }}
        suppressDragLeaveHidesColumns
        tooltipShowDelay={200}
        tooltipHideDelay={500}
        rowBuffer={10}
        suppressCellFocus
      />
    </div>
  );
};

export default AgGridTable;
